// Librairies
import { ChangeDetectionStrategy, Component, computed, inject, OnInit, signal, Signal, WritableSignal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { map, Observable, of, switchMap } from 'rxjs';
import { toError } from 'src/app/classes/shared/logs';
import { Pop } from 'src/app/classes/shared/pop';

// Services
import { ChartService } from 'src/app/services/api/charts/chart.service';
import { DashboardService } from 'src/app/services/api/dashboards/dashboard.service';
import { ProjectService } from 'src/app/services/api/projects/project.service';
import { AuthService } from 'src/app/services/core/auth.service';

// Interfaces
import { BY_PROJECT_WIDGET_TYPES, Chart, PostChart, PROJECT_ROWS_WIDGET_TYPES, TemplateMeta, TemplateTheme, WidgetV2 } from 'src/app/interfaces/api/chart';
import { SYNTHESIS_THEMES, SynthesisTheme, WidgetEditorScope } from 'src/app/interfaces/api/dashboard';
import { WIDGET_FAMILIES, WidgetFamily, WidgetFamilyKey, WidgetGraphType, popularGraphTypes, widgetTypeAllowed } from 'src/app/components/project-pages/dashboard/widget-types';

// Components
import { TemplateDialogComponent } from 'src/app/components/project-pages/dashboard/dialogs/template-dialog/template-dialog.component';
import { WidgetEditorComponent } from 'src/app/components/project-pages/dashboard/dialogs/widget-editor/widget-editor.component';
import { WidgetThumbnailComponent } from 'src/app/components/project-pages/dashboard/widget-thumbnail/widget-thumbnail.component';
import { NoDataComponent } from 'projects/ui-library/src/lib/components/no-data/no-data.component';
import { ButtonComponent, SearchBarComponent, SpinnerComponent } from 'ui-library';

// Interfaces
export interface WidgetTemplate extends WidgetV2 {
  description?: string;
  theme?: string;
}

// The open view: every widget type, the types of a family, every template, or the templates of a theme.
export type CatalogView = 'types' | WidgetFamilyKey | 'all' | SynthesisTheme;

export interface CatalogFamily {
  family: WidgetFamily;
  types: WidgetGraphType[];
}

export interface CatalogSection {
  theme: SynthesisTheme;
  templates: WidgetTemplate[];
}

// Where the copy goes: the editor data of the caller, plus the current user and project.
export interface CatalogTarget {
  scope?: WidgetEditorScope;
  dashboard?: any;
  isPersonal?: boolean;
  selectedProjects?: string[];
  userId?: string;
  projectId?: string;
}

// Variables
const THEME_ICONS: Record<SynthesisTheme, string> = { prevention: 'health_and_safety', production: 'insights', safety: 'crisis_alert', environment: 'eco' };
const TEMPLATE_PREFIX = /^(Graph|Texte|Heatmap|Chart|Table|Image|Video|Planning|Map):\s*/;
const KEPT_CONTENT_TYPES = new Set(['map', 'forge', 'planning3D']);

// Classes
export function templateTitle(template: WidgetTemplate): string {
  return (template.title ?? '').replace(TEMPLATE_PREFIX, '');
}


// A synthesis compares its projects in charts and tables, and always in heatmaps and maps.
export function byProjectInSynthesis(type?: string): boolean {
  return BY_PROJECT_WIDGET_TYPES.includes(type ?? '') || PROJECT_ROWS_WIDGET_TYPES.includes(type ?? '');
}

// The templates of the context, by theme: the empty themes (no template has one yet) are left out.
export function catalogSections(templates: WidgetTemplate[], scoped: boolean): CatalogSection[] {
  const allowed = templates.filter(template => widgetTypeAllowed(template.type, scoped));
  return SYNTHESIS_THEMES
    .map(theme => ({ theme, templates: allowed.filter(template => template.theme === theme) }))
    .filter(section => section.templates.length > 0);
}

// The copy of a template that the editor would save: not a template, same creation rules (project or synthesis).
export function templateCopy(template: WidgetTemplate, target: CatalogTarget): PostChart {
  const { id, createdDate, data, refresh, export: exported, theme, ...source } = template as any;
  const chart: any = JSON.parse(JSON.stringify(source));

  chart.template = false;
  chart.user = target.userId;
  if (!KEPT_CONTENT_TYPES.has(chart.type)) chart.content = {};

  if (target.scope) {
    chart.destination = 'company';
    chart.company = target.scope.companyId;
    chart.projects = [];
    chart.content = {};
    chart.query = { ...chart.query, byProject: byProjectInSynthesis(chart.type) };
  } else {
    chart.destination = target.dashboard?.owner ? 'user' : 'project';
    chart.projects = target.projectId ? [target.projectId] : [];
    if (target.isPersonal && target.selectedProjects?.length) chart.projects = [...target.selectedProjects];
  }

  return chart as PostChart;
}

// "Add a widget": the catalog first; a type or "Customize" then opens the editor. Same result as the editor.
export function openWidgetCatalog(dialog: MatDialog, data: CatalogTarget & Record<string, any>): Observable<any> {
  return dialog.open(WidgetCatalogComponent, { width: '1100px', maxWidth: '95vw', height: '85vh', data })
    .afterClosed()
    .pipe(switchMap(result => result?.editor
      ? dialog.open(WidgetEditorComponent, { width: '95%', height: '95%', data: { ...data, ...result.editor } }).afterClosed()
      : of(result)));
}

// Widget catalog: the views on the left (widget types, every template, the themes), their cards on the right.
@Component({
  selector: 'app-widget-catalog',
  standalone: true,
  imports: [
    FormsModule,
    MatIconModule,
    MatMenuModule,
    TranslateModule,
    ButtonComponent,
    NoDataComponent,
    SearchBarComponent,
    SpinnerComponent,
    WidgetThumbnailComponent
  ],
  templateUrl: './widget-catalog.component.html',
  styleUrl: './widget-catalog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})

export class WidgetCatalogComponent implements OnInit {
  // Services
  private readonly chartService: ChartService = inject(ChartService);
  private readonly dashboardService: DashboardService = inject(DashboardService);
  private readonly projectService: ProjectService = inject(ProjectService);
  private readonly auth: AuthService = inject(AuthService);
  private readonly pop: Pop = inject(Pop);
  private readonly translate: TranslateService = inject(TranslateService);
  private readonly dialogRef: MatDialogRef<WidgetCatalogComponent> = inject(MatDialogRef<WidgetCatalogComponent>);
  private readonly dialog: MatDialog = inject(MatDialog);
  readonly data: CatalogTarget = inject(MAT_DIALOG_DATA) ?? {};

  // Variables
  readonly scoped: boolean = !!this.data.scope;
  // Dev/admin: edit and delete the templates.
  readonly canManageTemplates: boolean = this.auth.isPrivileged();
  readonly graphTypes: WidgetGraphType[] = popularGraphTypes(this.scoped);
  // The families of the context (empty ones left out), their types the most used first.
  readonly families: CatalogFamily[] = WIDGET_FAMILIES
    .map(family => ({ family, types: this.graphTypes.filter(graphType => family.types.includes(graphType.value)) }))
    .filter(entry => entry.types.length > 0);

  // Signals
  readonly loading: WritableSignal<boolean> = signal(true);
  readonly saving: WritableSignal<boolean> = signal(false);
  readonly search: WritableSignal<string> = signal('');
  readonly templates: WritableSignal<WidgetTemplate[]> = signal([]);
  readonly view: WritableSignal<CatalogView> = signal('types');
  readonly selected: WritableSignal<WidgetTemplate | null> = signal(null);

  readonly sections: Signal<CatalogSection[]> = computed(() => catalogSections(this.templates(), this.scoped));

  // A view of widget types (all of them or a family), not of templates.
  readonly typesView: Signal<boolean> = computed(() => this.view() === 'types' || this.families.some(entry => entry.family.key === this.view()));

  // The widget types of the open view, by name.
  readonly types: Signal<WidgetGraphType[]> = computed(() => {
    const search = this.normalizedSearch();
    const types = this.families.find(entry => entry.family.key === this.view())?.types ?? this.graphTypes;
    return search ? types.filter(graphType => this.translate.instant(graphType.name).toLowerCase().includes(search)) : types;
  });

  // The templates of the open view, by title.
  readonly visibleTemplates: Signal<WidgetTemplate[]> = computed(() => {
    const view = this.view();
    const search = this.normalizedSearch();
    const templates = view === 'all'
      ? this.templates().filter(template => widgetTypeAllowed(template.type, this.scoped))
      : this.sections().find(section => section.theme === view)?.templates ?? [];
    return search ? templates.filter(template => templateTitle(template).toLowerCase().includes(search)) : templates;
  });

  private readonly normalizedSearch: Signal<string> = computed(() => this.search().trim().toLowerCase());

  ngOnInit(): void {
    this.loadTemplates();
  }

  loadTemplates(): void {
    this.chartService.getTemplates().subscribe({
      next: templates => {
        this.templates.set(templates ?? []);
        this.loading.set(false);
      },
      error: () => this.loading.set(false)
    });
  }

  title(template: WidgetTemplate): string {
    return templateTitle(template);
  }

  // Same icons as the synthesis tabs.
  themeIcon(theme: SynthesisTheme): string {
    return THEME_ICONS[theme];
  }

  themeLabel(theme: SynthesisTheme): string {
    return `synthesis.${theme}.title`;
  }

  forecast(template: WidgetTemplate): boolean {
    return !!template.query?.forecast?.enabled;
  }

  byProjectType(type: string): boolean {
    return byProjectInSynthesis(type);
  }

  byProject(template: WidgetTemplate): boolean {
    return this.scoped && byProjectInSynthesis(template.type);
  }

  selectView(view: CatalogView): void {
    this.view.set(view);
    this.selected.set(null);
  }

  select(template: WidgetTemplate): void {
    this.selected.set(template);
  }

  // A type: the blank editor of this type.
  chooseType(graphType: WidgetGraphType): void {
    this.dialogRef.close({ editor: { graphType: graphType.value } });
  }

  // The template, in the editor.
  customize(): void {
    const template = this.selected();
    if (template) this.dialogRef.close({ editor: { template: JSON.parse(JSON.stringify(template)) } });
  }

  // The template copied as is into the dashboard or the synthesis.
  add(): void {
    const template = this.selected();
    if (!template || this.saving()) return;

    const scope = this.data.scope;
    const dashboard = this.data.dashboard;
    const chart = templateCopy(template, {
      ...this.data,
      userId: this.auth.currentUser()?.id,
      projectId: this.projectService.currentProjectValue?.id
    });

    this.saving.set(true);
    this.chartService.addChart(chart).pipe(
      switchMap((created: Chart) => scope
        ? this.dashboardService.addCompanyChart(scope.companyId, scope.theme, created.id).pipe(map(companyDashboard => ({ companyDashboard })))
        : this.dashboardService.addChartToDashboard(dashboard.id, created.id).pipe(map(() => {
          const widget = { ...chart, id: created.id };
          dashboard.charts?.push(widget);
          return { widget, dashboard };
        })))
    ).subscribe({
      next: result => {
        this.pop.success(this.translate.instant('editor.chart_saved'));
        this.dialogRef.close(result);
      },
      error: (err: unknown) => {
        this.saving.set(false);
        this.pop.error(toError(err));
      }
    });
  }

  // Name, theme and description: the dialog of "Save as template", saved as is.
  editTemplateInfo(template: WidgetTemplate): void {
    const data = { title: template.title, theme: template.theme as TemplateTheme, description: template.description, editing: true };

    this.dialog.open(TemplateDialogComponent, { width: '480px', maxWidth: '95vw', data }).afterClosed().subscribe((meta?: TemplateMeta) => {
      if (!meta || !template.id) return;

      this.chartService.updateTemplate(template.id, meta).subscribe({
        next: () => {
          this.pop.success(this.translate.instant('catalog.template_saved'));
          this.loadTemplates();
        },
        error: (err: unknown) => this.pop.error(toError(err))
      });
    });
  }

  // The editor on the template itself; its save updates the template.
  editTemplate(template: WidgetTemplate): void {
    this.dialog.open(WidgetEditorComponent, {
      width: '95%',
      height: '95%',
      data: { ...this.data, widget: JSON.parse(JSON.stringify(template)), editingTemplate: true }
    }).afterClosed().subscribe(result => {
      if (result?.template) this.loadTemplates();
    });
  }

  deleteTemplate(template: WidgetTemplate): void {
    this.pop.confirm(this.translate.instant('catalog.confirm_delete_template'), (confirm: any) => {
      if (!confirm.ok || !template.id) return;

      this.chartService.deleteTemplate(template.id).subscribe({
        next: () => {
          if (this.selected()?.id === template.id) this.selected.set(null);
          this.pop.success(this.translate.instant('catalog.template_deleted'));
          this.loadTemplates();
        },
        error: (err: unknown) => this.pop.error(toError(err))
      });
    });
  }

  close(): void {
    this.dialogRef.close();
  }
}
